<div class="page-layout">
  <app-sidebar></app-sidebar>

  <div class="main-area">
    <app-navbar pageTitle="Brain Activity" pageSubtitle="How the brain is being queried, live"></app-navbar>

    <div class="content-wrap">

      <!-- KPI row -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-icon blue-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <span class="stat-value">{{ summary()?.queriesToday ?? '—' }}</span>
          <span class="stat-label">Queries Today</span>
          <span class="stat-delta" *ngIf="summary() as s">{{ s.queriesDeltaPct >= 0 ? '↑' : '↓' }} {{ s.queriesDeltaPct }}% vs yesterday</span>
        </div>

        <div class="stat-card">
          <div class="stat-icon purple-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A855F7" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span class="stat-value">{{ summary() ? (summary()!.avgResponseMs / 1000).toFixed(1) + 's' : '—' }}</span>
          <span class="stat-label">Avg. Response Time</span>
        </div>

        <div class="stat-card">
          <div class="stat-icon teal-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#14B8A6" stroke-width="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </div>
          <span class="stat-value">{{ summary()?.activeConnectors?.length ?? '—' }}</span>
          <span class="stat-label">Active Connectors</span>
          <span class="stat-delta neutral" *ngIf="summary() as s">{{ s.activeConnectors.join(' · ') }}</span>
        </div>

        <div class="stat-card">
          <div class="stat-icon orange-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F97316" stroke-width="2">
              <rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="2" width="8" height="8" rx="1"/>
              <rect x="2" y="14" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/>
            </svg>
          </div>
          <span class="stat-value">{{ summary()?.avgToolsPerQuery ?? '—' }}</span>
          <span class="stat-label">Avg. Tools per Query</span>
        </div>
      </div>

      <!-- 3-column body -->
      <div class="content-grid">

        <!-- LEFT: connected tools -->
        <div class="tools-panel">
          <div class="panel-header">
            <h3 class="panel-title">Connected Tools</h3>
            <p class="panel-subtitle">What the brain can query</p>
          </div>

          <ng-container *ngIf="connectedTools() as t; else toolsLoading">

            <!-- Knowledge Graph -->
            <div class="tool-card">
              <button type="button" class="tool-header" (click)="toggleKg()">
                <span class="tool-icon-wrap kg-color">
                  <img src="/assets/icons/knowledge-graph.svg" width="18" height="18" alt="" />
                </span>
                <span class="tool-info">
                  <span class="tool-name">Knowledge Graph</span>
                  <span class="tool-meta">{{ t.knowledgeGraph.totalNodes | number }} nodes · {{ t.knowledgeGraph.totalEdges | number }} edges</span>
                </span>
                <span class="status-dot dot-on"></span>
                <svg class="chevron" [class.open]="kgExpanded()" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A0A8BF" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div class="tool-items" *ngIf="kgExpanded()">
                <div class="table-row">
                  <span class="entity-name">Nodes</span>
                  <span class="entity-count">{{ t.knowledgeGraph.totalNodes | number }}</span>
                </div>
                <div class="table-row">
                  <span class="entity-name">Edges</span>
                  <span class="entity-count">{{ t.knowledgeGraph.totalEdges | number }}</span>
                </div>
                <div class="table-row">
                  <span class="entity-name">Documents</span>
                  <span class="entity-count">{{ t.knowledgeGraph.totalDocuments | number }}</span>
                </div>
              </div>
            </div>

            <!-- SQL Database -->
            <div class="tool-card">
              <button type="button" class="tool-header" (click)="toggleSql()">
                <span class="tool-icon-wrap sql-color">
                  <img src="/assets/icons/sources/database.svg" width="18" height="18" alt="" />
                </span>
                <span class="tool-info">
                  <span class="tool-name">SQL Database</span>
                  <span class="tool-meta">{{ t.sqlDatabase.length }} tables · {{ sqlTotalRows(t) | number }} rows</span>
                </span>
                <span class="status-dot dot-on"></span>
                <svg class="chevron" [class.open]="sqlExpanded()" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A0A8BF" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div class="tool-items" *ngIf="sqlExpanded()">
                <div class="table-row" *ngFor="let tb of t.sqlDatabase">
                  <span class="entity-name">{{ tb.name }}</span>
                  <span class="entity-count">{{ tb.count | number }}</span>
                </div>
                <div class="empty-row" *ngIf="t.sqlDatabase.length === 0">No tables connected yet.</div>
              </div>
            </div>

            <!-- Vector DB -->
            <div class="tool-card">
              <button type="button" class="tool-header" (click)="toggleVector()">
                <span class="tool-icon-wrap vec-color">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M12 2l2.2 6.8L21 11l-6.8 2.2L12 20l-2.2-6.8L3 11l6.8-2.2L12 2z"/>
                  </svg>
                </span>
                <span class="tool-info">
                  <span class="tool-name">Vector DB · Weaviate</span>
                  <span class="tool-meta">{{ t.vectorDb.length }} collections</span>
                </span>
                <span class="status-dot dot-on"></span>
                <svg class="chevron" [class.open]="vectorExpanded()" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A0A8BF" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              <div class="tool-items" *ngIf="vectorExpanded()">
                <div class="table-row" *ngFor="let c of t.vectorDb">
                  <span class="entity-name">{{ c.name }}</span>
                  <span class="entity-count">{{ c.objects | number }}</span>
                </div>
                <div class="empty-row" *ngIf="t.vectorDb.length === 0">No collections yet.</div>
              </div>
            </div>

          </ng-container>
          <ng-template #toolsLoading>
            <p class="empty-row">No tool data available yet.</p>
          </ng-template>
        </div>

        <!-- CENTER: static hub -->
        <div class="hub-panel">
          <div class="panel-header center-header">
            <h3 class="panel-title">Connected Surfaces</h3>
            <p class="panel-subtitle">Everything that talks to the brain</p>
          </div>

          <div class="hub-box">
            <svg class="hub-svg" viewBox="0 0 600 300" preserveAspectRatio="xMidYMid meet">
              <defs>
                <radialGradient id="baGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"  stop-color="#2DD4BF" stop-opacity="0.35"/>
                  <stop offset="60%" stop-color="#0D9488" stop-opacity="0.12"/>
                  <stop offset="100%" stop-color="#0D9488" stop-opacity="0"/>
                </radialGradient>
                <filter id="baSparkGlow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="2" result="blur"/>
                  <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              </defs>

              <!-- circuit traces from each connector into the brain -->
              <path class="ba-trace" d="M70,60 H170 V150 H250"/>
              <path class="ba-trace" d="M530,60 H430 V150 H350"/>
              <path class="ba-trace" d="M70,240 H170 V150 H250"/>
              <path class="ba-trace" d="M530,240 H430 V150 H350"/>

              <!-- via markers along the traces -->
              <rect class="ba-via" x="167" y="57"  width="6" height="6"/>
              <rect class="ba-via" x="167" y="147" width="6" height="6"/>
              <rect class="ba-via" x="427" y="57"  width="6" height="6"/>
              <rect class="ba-via" x="427" y="147" width="6" height="6"/>
              <rect class="ba-via" x="167" y="237" width="6" height="6"/>
              <rect class="ba-via" x="427" y="237" width="6" height="6"/>

              <!-- API -->
              <circle class="ba-node-ring api-ring" cx="70" cy="60" r="26"/>
              <svg x="57" y="47" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#3E8E92" stroke-width="2">
                <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                <polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
              <text x="70" y="102" text-anchor="middle" class="ba-node-label">API</text>

              <!-- Chatbot -->
              <circle class="ba-node-ring chat-ring" cx="530" cy="60" r="26"/>
              <svg x="517" y="47" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2E8FE0" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <text x="530" y="102" text-anchor="middle" class="ba-node-label">Chatbot</text>

              <!-- Inference -->
              <circle class="ba-node-ring inf-ring" cx="70" cy="240" r="26"/>
              <image x="57" y="227" width="26" height="26" href="/assets/icons/inferences.svg"/>
              <text x="70" y="282" text-anchor="middle" class="ba-node-label">Inference</text>

              <!-- Classification -->
              <circle class="ba-node-ring cls-ring" cx="530" cy="240" r="26"/>
              <image x="517" y="227" width="26" height="26" href="/assets/icons/classification.svg"/>
              <text x="530" y="282" text-anchor="middle" class="ba-node-label">Classification</text>

              <!-- Brain core -->
              <circle cx="300" cy="150" r="140" fill="url(#baGlow)"/>
              <circle cx="300" cy="150" r="92" class="ba-ring-outer"/>
              <circle cx="300" cy="150" r="76" class="ba-ring-inner"/>
              <circle cx="300" cy="150" r="64" class="ba-core-disc"/>

              <svg x="265" y="115" width="70" height="70" viewBox="0 0 24 24" fill="none" stroke="#0D9488" stroke-width="1.5">
                <path d="M9.5 2a3.5 3.5 0 0 0-3.5 3.5v.6A3.5 3.5 0 0 0 4 9.3v1.4a3.5 3.5 0 0 0-1 6.6 3.5 3.5 0 0 0 3.5 3.2A3.5 3.5 0 0 0 9.5 22a3.5 3.5 0 0 0 3.5-3.5V5.5A3.5 3.5 0 0 0 9.5 2Z"/>
                <path d="M14.5 2a3.5 3.5 0 0 1 3.5 3.5v.6A3.5 3.5 0 0 1 20 9.3v1.4a3.5 3.5 0 0 1 1 6.6 3.5 3.5 0 0 1-3.5 3.2A3.5 3.5 0 0 1 14.5 22a3.5 3.5 0 0 1-3.5-3.5V5.5A3.5 3.5 0 0 1 14.5 2Z"/>
              </svg>

              <!-- lit synapse network over the brain glyph -->
              <g class="ba-synapse-net">
                <line x1="292.7" y1="132.5" x2="307.3" y2="132.5"/>
                <line x1="292.7" y1="132.5" x2="279.6" y2="150"/>
                <line x1="307.3" y1="132.5" x2="320.4" y2="150"/>
                <line x1="279.6" y1="150" x2="300" y2="170.4"/>
                <line x1="320.4" y1="150" x2="300" y2="170.4"/>
              </g>
              <g class="ba-sparks" filter="url(#baSparkGlow)">
                <circle cx="292.7" cy="132.5" r="2.6"/>
                <circle cx="307.3" cy="132.5" r="2.6"/>
                <circle cx="279.6" cy="150" r="2.4"/>
                <circle cx="320.4" cy="150" r="2.4"/>
                <circle cx="300" cy="170.4" r="2.8"/>
              </g>

              <text x="300" y="244" text-anchor="middle" class="ba-brain-label">Brain</text>
            </svg>

            <button type="button" class="hub-center-hit" (click)="openDetail()" aria-label="Open Brain Activity detail"></button>
          </div>

          <p class="hub-caption">Click the brain to see how a query moves through it.</p>
        </div>

        <!-- RIGHT: most frequent -->
        <div class="frequent-panel">
          <div class="panel-header">
            <h3 class="panel-title">Most Frequent</h3>
            <p class="panel-subtitle">Across every connector, last 24h</p>
          </div>

          <div class="freq-item" *ngFor="let q of frequentQueries(); let i = index">
            <span class="freq-rank">{{ (i + 1) < 10 ? '0' + (i + 1) : (i + 1) }}</span>
            <div class="freq-body">
              <span class="freq-text" [title]="q.queryPattern">{{ q.queryPattern }}</span>
              <div class="freq-bar">
                <span class="seg chat-seg" [style.width.%]="connectorSegmentWidth(q.connectorBreakdown.chatbot, q)"></span>
                <span class="seg inf-seg" [style.width.%]="connectorSegmentWidth(q.connectorBreakdown.inference, q)"></span>
                <span class="seg api-seg" [style.width.%]="connectorSegmentWidth(q.connectorBreakdown.api, q)"></span>
              </div>
              <span class="freq-meta">{{ q.count }} queries · last seen {{ q.lastSeen }}</span>
            </div>
            <span class="freq-count">{{ q.count }}</span>
          </div>

          <p class="empty-row" *ngIf="frequentQueries().length === 0">No query activity yet.</p>
        </div>

      </div>
    </div>
  </div>
</div>
